import { useContext } from '@nuxtjs/composition-api'
import { domainByCountry, leaveShopOnCurrentDomain } from '~/assets/ts/Domains'
import { getCountryCode } from '~/assets/ts/Locale'

export class AppointmentLink {
  /**
   * Liefert die Termin-Vereinbaren-Übersichtsseite, oder
   * wenn ein Standard-Shop für das Land in der Env-Variable
   * definiert wurde, wird direkt die Termin-Seite dieses Shops
   * aufgerufen.
   *
   * Der Rückgabewert ist ein Objekt das sowohl die URL als auch
   * den passenden Wert für das target-Attribut beinhaltet.
   */
  getAppointmentOverview(): {
    url: string
    target: string
    individual: boolean
  } {
    const {
      app: { localePath },
    } = useContext()

    const individuellLink: {
      url: string
      target: string
      individual: boolean
    } = this.getIndividuellLinkByCountry(getCountryCode(true))

    return individuellLink.url === ''
      ? {
          url: localePath({
            name: 'shops',
            params: {},
          }),
          target: '_self',
          individual: false,
        }
      : individuellLink
  }

  /**
   * Liefert den Link zu Termin-Vereinbaren-Seite.
   *
   * Prüft dabei ob,
   * - die Website auf die Länderversion gewechselt werden soll
   * - ob ein Land oder Shop eine individuelle externe Seite hat
   * - ob ein Land immer auf einen Shop geleitet werden soll.
   *
   * Sollte keines davon passen wird der Link zur Termin-Seite
   * generiert und zurückgegeben.
   *
   * Der Rückgabewert ist ein Objekt das sowohl die URL als auch
   * den passenden Wert für das target-Attribut beinhaltet.
   *
   * @param country
   * @param shopId
   * @param shopSlug
   */
  getAppointmentLink(
    country: string,
    shopId: number,
    shopSlug: string
  ): {
    url: string
    target: string
    individual: boolean
  } {
    const {
      app: { localePath },
    } = useContext()

    if (leaveShopOnCurrentDomain(country, shopId)) {
      return {
        url: domainByCountry(country, true, shopId) + shopSlug,
        target: '_blank',
        individual: true,
      }
    } else {
      const individuellLink: {
        url: string
        target: string
        individual: boolean
      } = this.getIndividuellLink(country, shopId)

      return individuellLink.url === ''
        ? {
            url: localePath({
              name: 'shopsDetails',
              params: { town: shopSlug },
            }),
            target: '_self',
            individual: false,
          }
        : individuellLink
    }
  }

  /**
   * Diese Funktion prüft anhand des Landes und einer Shop-ID
   * ob, eine externe Termin-Seite genutzt werden soll, oder ob
   * für ein Land direkt ein bestimmter Shop gezeigt werden soll.
   *
   * Ist keine individuelle Termin-Seite vorhanden, wird ein
   * leerer String zurückgegeben.
   *
   * @param country
   * @param shopId
   * @return string
   */
  getIndividuellLink(
    country: string,
    shopId: number
  ): {
    url: string
    target: string
    individual: boolean
  } {
    let externalLink: {
      url: string
      target: string
      individual: boolean
    } = this.getIndividuellLinkByShopId(shopId)

    if (externalLink.url === '') {
      externalLink = this.getIndividuellLinkByCountry(country)
    }

    return externalLink
  }

  private getIndividuellLinkByShopId(shopId: number): {
    url: string
    target: string
    individual: boolean
  } {
    let appointmentLink: string = ''
    switch (parseInt(shopId.toString())) {
      case 10561: // VEIL Fukuyama
      case 10738: // VEIL Hiroshima
        appointmentLink = 'https://veil-bridal.com/reservation/'
        break
      case 10451: // Koriyama
        appointmentLink = 'http://opera-kinbido.com/reserve/'
        break
      case 10768: // 6-5-8 1F Ginza, Chuo-ku
        appointmentLink = 'https://coubic.com/acredo#pageContent'
        break
      case 10281: // Waterloo
        appointmentLink = 'https://loverings.be/contactez-nous/'
        break
      case 10805: // Singapore
        appointmentLink = 'https://www.aluxe.com/sg-en/reservation/'
        break
    }
    return {
      url: appointmentLink,
      target: '_blank',
      individual: true,
    }
  }

  private getIndividuellLinkByCountry(country: string): {
    url: string
    target: string
    individual: boolean
  } {
    const countryDefaultShop: { [key: string]: string } = {
      dk: 'roskilde',
      ee: 'tallinn',
      us: 'denver',
      sg: 'orchard-singapore',
    }

    const countryExternalLink: { [key: string]: string } = {
      kr: 'https://www.acredokorea.com/37',
      hk: 'https://www.aluxe.com/reservation/',
      sg: 'https://www.aluxe.com/sg-en/reservation/',
      tw: 'https://www.aluxe.com/reservation/',
      beFr: 'https://loverings.be/contactez-nous/',
    }

    if (typeof countryExternalLink[country] === 'string') {
      return {
        url: countryExternalLink[country],
        target: '_blank',
        individual: true,
      }
    } else if (typeof countryDefaultShop[country] === 'string') {
      const {
        app: { localePath },
      } = useContext()

      return {
        url: localePath({
          name: 'shopsDetails',
          params: { town: countryDefaultShop[country] },
        }),
        target: '_self',
        individual: false,
      }
    } else {
      return {
        url: '',
        target: '_self',
        individual: false,
      }
    }
  }
}
